import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MAIN_MARGIN = { top: 20, right: 30, bottom: 50, left: 60 };
const PHASE_MARGIN = { top: 10, right: 30, bottom: 40, left: 60 };
const MAIN_HEIGHT = 280;
const PHASE_HEIGHT = 140;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexShrink: 0,
    marginLeft: '16px',
  },
  select: {
    padding: '4px 8px',
    fontSize: '11px',
    fontWeight: 500,
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  sectionLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  container: {
    overflowX: 'auto',
  },
  summaryRow: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid #f3f4f6',
  },
  summaryItem: {
    fontSize: '12px',
    color: '#6b7280',
  },
  summaryValue: {
    fontWeight: 600,
    color: '#1f2937',
    marginLeft: '4px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatPairLabel(field1, field2) {
  return `${field1} vs ${field2}`;
}

export default function SpectralCoherenceChart({ data }) {
  const mainSvgRef = useRef(null);
  const phaseSvgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [selectedPairIndex, setSelectedPairIndex] = useState(0);

  const pairs = data && data.pairs ? data.pairs : [];
  const significanceThreshold = data && data.significanceThreshold != null
    ? data.significanceThreshold
    : 0.5;

  const selectedPair = pairs.length > 0 && selectedPairIndex >= 0 && selectedPairIndex < pairs.length
    ? pairs[selectedPairIndex]
    : null;

  // Draw main coherence chart
  useEffect(() => {
    if (!selectedPair || !selectedPair.frequencies || selectedPair.frequencies.length === 0) return;

    const svg = d3.select(mainSvgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MAIN_MARGIN.left - MAIN_MARGIN.right;
    const innerH = MAIN_HEIGHT - MAIN_MARGIN.top - MAIN_MARGIN.bottom;

    svg.attr('width', width).attr('height', MAIN_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MAIN_MARGIN.left}, ${MAIN_MARGIN.top})`);

    const freqs = selectedPair.frequencies;
    const coherence = selectedPair.coherence;
    const periods = selectedPair.periods;

    // Build data points
    const points = freqs.map((freq, i) => ({
      freq,
      coherence: coherence[i],
      period: periods ? periods[i] : null,
      phase: selectedPair.phase ? selectedPair.phase[i] : null,
    }));

    // Scales
    const freqExtent = d3.extent(freqs);
    const freqPad = (freqExtent[1] - freqExtent[0]) * 0.05 || 0.01;
    const x = d3.scaleLinear()
      .domain([Math.max(0, freqExtent[0] - freqPad), freqExtent[1] + freqPad])
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([0, 1])
      .range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = [0, 0.2, 0.4, 0.6, 0.8, 1.0];
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Significance threshold line (dashed)
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', y(significanceThreshold))
      .attr('y2', y(significanceThreshold))
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,4');

    // Threshold label
    g.append('text')
      .attr('x', innerW - 4)
      .attr('y', y(significanceThreshold) - 6)
      .attr('text-anchor', 'end')
      .attr('font-size', '9px')
      .attr('fill', '#9ca3af')
      .text(`Significance: ${significanceThreshold}`);

    // Split the area into above-threshold (blue) and below-threshold (gray)
    // Filled area above threshold - clip to significant region
    const clipId = `clip-above-${Date.now()}`;
    g.append('defs')
      .append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', innerW)
      .attr('height', y(significanceThreshold));

    const clipBelowId = `clip-below-${Date.now()}`;
    g.append('defs')
      .append('clipPath')
      .attr('id', clipBelowId)
      .append('rect')
      .attr('x', 0)
      .attr('y', y(significanceThreshold))
      .attr('width', innerW)
      .attr('height', innerH - y(significanceThreshold));

    // Area generator
    const areaGen = d3.area()
      .x((d) => x(d.freq))
      .y0(innerH)
      .y1((d) => y(d.coherence))
      .curve(d3.curveMonotoneX);

    // Blue fill for above threshold
    g.append('path')
      .datum(points)
      .attr('d', areaGen)
      .attr('fill', '#2563eb')
      .attr('fill-opacity', 0.15)
      .attr('stroke', 'none')
      .attr('clip-path', `url(#${clipId})`);

    // Gray fill for below threshold
    g.append('path')
      .datum(points)
      .attr('d', areaGen)
      .attr('fill', '#9ca3af')
      .attr('fill-opacity', 0.08)
      .attr('stroke', 'none')
      .attr('clip-path', `url(#${clipBelowId})`);

    // Line on top
    const lineGen = d3.line()
      .x((d) => x(d.freq))
      .y((d) => y(d.coherence))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(points)
      .attr('d', lineGen)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.9);

    // Peak coherence annotation
    if (selectedPair.peakCoherence != null && selectedPair.peakFrequency != null) {
      const peakFreq = selectedPair.peakFrequency;
      const peakCoh = selectedPair.peakCoherence;
      const peakPeriod = selectedPair.peakPeriod;

      // Vertical dashed line at peak
      g.append('line')
        .attr('x1', x(peakFreq))
        .attr('x2', x(peakFreq))
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#2563eb')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,3')
        .attr('stroke-opacity', 0.5);

      // Peak dot
      g.append('circle')
        .attr('cx', x(peakFreq))
        .attr('cy', y(peakCoh))
        .attr('r', 5)
        .attr('fill', '#2563eb')
        .attr('stroke', '#fff')
        .attr('stroke-width', 2);

      // Peak annotation
      const labelX = x(peakFreq) + 10;
      const labelY = y(peakCoh) - 8;
      const labelAnchor = labelX + 100 > innerW ? 'end' : 'start';
      const effectiveLabelX = labelX + 100 > innerW ? x(peakFreq) - 10 : labelX;

      g.append('text')
        .attr('x', effectiveLabelX)
        .attr('y', labelY)
        .attr('text-anchor', labelAnchor)
        .attr('font-size', '9px')
        .attr('font-weight', 600)
        .attr('fill', '#2563eb')
        .text(`Peak: ${peakCoh.toFixed(2)} @ f=${peakFreq}${peakPeriod != null ? ` (T=${peakPeriod.toFixed(1)})` : ''}`);
    }

    // X axis (frequency)
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).ticks(8).tickFormat((v) => v.toFixed(2)))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Period labels below frequency axis
    const periodTicks = points.filter((p) => p.period != null);
    const periodSubset = periodTicks.filter((_, i) => i % 2 === 0 || i === periodTicks.length - 1);

    g.selectAll('.period-tick')
      .data(periodSubset)
      .enter()
      .append('text')
      .attr('x', (d) => x(d.freq))
      .attr('y', innerH + 28)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('fill', '#9ca3af')
      .text((d) => `${d.period.toFixed(1)}`);

    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 42)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#9ca3af')
      .text('Period (scrape intervals)');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .tickValues([0, 0.2, 0.4, 0.6, 0.8, 1.0])
          .tickSize(0)
          .tickFormat((v) => v.toFixed(1))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -44)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Coherence');

    // Invisible hover dots
    points.forEach((d) => {
      g.append('circle')
        .attr('cx', x(d.freq))
        .attr('cy', y(d.coherence))
        .attr('r', 8)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            frequency: d.freq,
            period: d.period,
            coherence: d.coherence,
            phase: d.phase,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [selectedPair, significanceThreshold]);

  // Draw phase difference chart
  useEffect(() => {
    if (!selectedPair || !selectedPair.frequencies || selectedPair.frequencies.length === 0 || !selectedPair.phase) {
      const svg = d3.select(phaseSvgRef.current);
      svg.selectAll('*').remove();
      return;
    }

    const svg = d3.select(phaseSvgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - PHASE_MARGIN.left - PHASE_MARGIN.right;
    const innerH = PHASE_HEIGHT - PHASE_MARGIN.top - PHASE_MARGIN.bottom;

    svg.attr('width', width).attr('height', PHASE_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${PHASE_MARGIN.left}, ${PHASE_MARGIN.top})`);

    const freqs = selectedPair.frequencies;
    const phase = selectedPair.phase;
    const coherence = selectedPair.coherence;

    const points = freqs.map((freq, i) => ({
      freq,
      phase: phase[i],
      coherence: coherence[i],
    }));

    // Scales
    const freqExtent = d3.extent(freqs);
    const freqPad = (freqExtent[1] - freqExtent[0]) * 0.05 || 0.01;
    const x = d3.scaleLinear()
      .domain([Math.max(0, freqExtent[0] - freqPad), freqExtent[1] + freqPad])
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([-Math.PI, Math.PI])
      .range([innerH, 0]);

    // Horizontal grid lines
    const phaseGridValues = [-Math.PI, -Math.PI / 2, 0, Math.PI / 2, Math.PI];
    g.selectAll('.grid-line')
      .data(phaseGridValues)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Zero line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', y(0))
      .attr('y2', y(0))
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3');

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).ticks(8).tickFormat((v) => v.toFixed(2)))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis label
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 30)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Frequency');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .tickValues([-Math.PI, -Math.PI / 2, 0, Math.PI / 2, Math.PI])
          .tickSize(0)
          .tickFormat((v) => {
            if (Math.abs(v - Math.PI) < 0.01) return '\u03C0';
            if (Math.abs(v + Math.PI) < 0.01) return '-\u03C0';
            if (Math.abs(v - Math.PI / 2) < 0.01) return '\u03C0/2';
            if (Math.abs(v + Math.PI / 2) < 0.01) return '-\u03C0/2';
            return '0';
          })
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -44)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Phase (rad)');

    // Dots colored by coherence: blue for significant, gray otherwise
    points.forEach((d) => {
      const isSignificant = d.coherence >= significanceThreshold;
      const color = isSignificant ? '#2563eb' : '#9ca3af';

      g.append('circle')
        .attr('cx', x(d.freq))
        .attr('cy', y(d.phase))
        .attr('r', isSignificant ? 4 : 3)
        .attr('fill', color)
        .attr('fill-opacity', isSignificant ? 0.8 : 0.4)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1);
    });

    // Connect significant points with a line
    const significantPoints = points.filter((d) => d.coherence >= significanceThreshold);
    if (significantPoints.length > 1) {
      const phaseLine = d3.line()
        .x((d) => x(d.freq))
        .y((d) => y(d.phase))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(significantPoints)
        .attr('d', phaseLine)
        .attr('fill', 'none')
        .attr('stroke', '#2563eb')
        .attr('stroke-width', 1)
        .attr('stroke-opacity', 0.4)
        .attr('stroke-dasharray', '3,3');
    }

    // Invisible hover targets
    points.forEach((d) => {
      g.append('circle')
        .attr('cx', x(d.freq))
        .attr('cy', y(d.phase))
        .attr('r', 8)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            frequency: d.freq,
            period: selectedPair.periods ? selectedPair.periods[selectedPair.frequencies.indexOf(d.freq)] : null,
            coherence: d.coherence,
            phase: d.phase,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [selectedPair, significanceThreshold]);

  // Empty state
  if (!data || !data.pairs || data.pairs.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.headerText}>
            <div style={styles.title}>Spectral Coherence</div>
            <div style={styles.subtitle}>Shared frequency patterns between price fields</div>
          </div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.headerText}>
          <div style={styles.title}>Spectral Coherence</div>
          <div style={styles.subtitle}>Shared frequency patterns between price fields</div>
        </div>
        <div style={styles.controls}>
          <select
            style={styles.select}
            value={selectedPairIndex}
            onChange={(e) => setSelectedPairIndex(Number(e.target.value))}
          >
            {pairs.map((pair, i) => (
              <option key={i} value={i}>
                {formatPairLabel(pair.field1, pair.field2)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Main coherence chart */}
      <div style={styles.sectionLabel}>Coherence vs Frequency</div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={mainSvgRef} />
      </div>

      {/* Phase difference chart */}
      {selectedPair && selectedPair.phase && (
        <>
          <div style={{ ...styles.sectionLabel, marginTop: '16px' }}>Phase Difference</div>
          <div style={styles.container}>
            <svg ref={phaseSvgRef} />
          </div>
        </>
      )}

      {/* Summary stats */}
      {selectedPair && (
        <div style={styles.summaryRow}>
          <div style={styles.summaryItem}>
            Pair:
            <span style={styles.summaryValue}>
              {formatPairLabel(selectedPair.field1, selectedPair.field2)}
            </span>
          </div>
          {selectedPair.peakCoherence != null && (
            <div style={styles.summaryItem}>
              Peak coherence:
              <span style={styles.summaryValue}>{selectedPair.peakCoherence.toFixed(3)}</span>
            </div>
          )}
          {selectedPair.peakFrequency != null && (
            <div style={styles.summaryItem}>
              Peak frequency:
              <span style={styles.summaryValue}>{selectedPair.peakFrequency.toFixed(3)}</span>
            </div>
          )}
          {selectedPair.peakPeriod != null && (
            <div style={styles.summaryItem}>
              Peak period:
              <span style={styles.summaryValue}>{selectedPair.peakPeriod.toFixed(1)} intervals</span>
            </div>
          )}
          <div style={styles.summaryItem}>
            Significance threshold:
            <span style={styles.summaryValue}>{significanceThreshold}</span>
          </div>
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {selectedPair ? formatPairLabel(selectedPair.field1, selectedPair.field2) : ''}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Frequency</span>
            <span style={{ fontWeight: 600 }}>{tooltip.frequency.toFixed(3)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Period</span>
            <span style={{ fontWeight: 600 }}>
              {tooltip.period != null ? `${tooltip.period.toFixed(1)} intervals` : 'N/A'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Coherence</span>
            <span style={{
              fontWeight: 600,
              color: tooltip.coherence >= significanceThreshold ? '#2563eb' : '#9ca3af',
            }}>
              {tooltip.coherence.toFixed(3)}
              {tooltip.coherence >= significanceThreshold ? ' (significant)' : ''}
            </span>
          </div>
          {tooltip.phase != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Phase</span>
              <span style={{ fontWeight: 600 }}>{tooltip.phase.toFixed(3)} rad</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
